import OnboardingProgressModel from "../../../shared/models/onboarding/api/OnboardingProgressModel";

export type OnboardingPhase = "onboarding" | "testing";

export interface TestingStepFeedbackHistoryEntry {
    value: string;
    savedAt: string;
}

export interface TestingProgressState {
    currentStepId?: string;
    completedStepIds?: string[];
    startedAt?: string;
    completedAt?: string;
    stepFeedbackByStepId?: Record<string, string>;
    stepNoFeedbackByStepId?: Record<string, boolean>;
    stepFeedbackHistoryByStepId?: Record<string, TestingStepFeedbackHistoryEntry[]>;
    feedbackFixedStatus?: Record<string, boolean>;
    feedbackInternalFixNotes?: Record<string, string>;
}

export interface TestingFeedbackResolutionState {
    fixedStatus: Record<string, boolean>;
    internalFixNotes: Record<string, string>;
}

export interface OnboardingSnapshotState {
    currentStep?: string;
    percentComplete?: number;
    isComplete?: boolean;
}

export interface LocationSyncState {
    status: "queued" | "failed";
    attemptedAt: string;
    error?: string;
}

export interface OnboardingSubmissionCheckpoint {
    version: 1;
    attemptId: string;
    createdAt: string;
    variables: string[];
    idempotencyKey?: string;
    payloadSignature?: string;
    originalSeedValues?: Record<string, string>;
    preSentinelValues?: Record<string, string>;
    targetLocationApplicationIds?: string[];
    templateConfigurationId: string;
    coreTemplateConfigurationId?: string;
}

export interface OnboardingProgressDetails {
    answers?: Record<string, unknown>;
    flowType?: string;
    lastSavedAt?: string;
    phase?: OnboardingPhase;
    testing?: TestingProgressState;
    onboardingSnapshot?: OnboardingSnapshotState;
    testingEnabledAt?: string;
    submissionCheckpoint?: OnboardingSubmissionCheckpoint;
    locationSync?: LocationSyncState;
    [key: string]: any;
}

export const parseOnboardingProgressDetails = (detailsJson?: string): OnboardingProgressDetails => {
    if (!detailsJson?.trim()) {
        return {};
    }

    try {
        const parsed = JSON.parse(detailsJson);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
};

export const serializeOnboardingProgressDetails = (details: OnboardingProgressDetails): string => {
    return JSON.stringify(details);
};

export const shouldShowOnboardingReview = (progress?: OnboardingProgressModel | null): boolean => {
    return !!progress?.isComplete || (!progress?.isComplete && (progress?.percentComplete || 0) >= 100);
};

export const isConfirmedMissingOnboardingProgress = (result?: {
    resultType?: string;
    data?: unknown;
}): boolean => {
    return result?.resultType === "NotFound"
        || (result?.resultType === "Ok" && !result.data);
};

export const getOnboardingSubmissionCheckpoint = (
    detailsJson?: string
): OnboardingSubmissionCheckpoint | undefined => {
    const checkpoint = parseOnboardingProgressDetails(detailsJson).submissionCheckpoint;
    if (
        !checkpoint
        || typeof checkpoint !== "object"
        || checkpoint.version !== 1
        || typeof checkpoint.attemptId !== "string"
        || !checkpoint.attemptId.trim()
        || typeof checkpoint.createdAt !== "string"
        || !Array.isArray(checkpoint.variables)
        || typeof checkpoint.templateConfigurationId !== "string"
        || !checkpoint.templateConfigurationId
    ) {
        return undefined;
    }

    const originalSeedValues = checkpoint.originalSeedValues
        && typeof checkpoint.originalSeedValues === "object"
        && !Array.isArray(checkpoint.originalSeedValues)
        ? checkpoint.originalSeedValues
        : {};
    const preSentinelValues = checkpoint.preSentinelValues
        && typeof checkpoint.preSentinelValues === "object"
        && !Array.isArray(checkpoint.preSentinelValues)
        ? checkpoint.preSentinelValues
        : {};
    const targetLocationApplicationIds = Array.isArray(checkpoint.targetLocationApplicationIds)
        ? checkpoint.targetLocationApplicationIds
        : [];

    return {
        version: 1,
        attemptId: checkpoint.attemptId,
        createdAt: checkpoint.createdAt,
        idempotencyKey: typeof checkpoint.idempotencyKey === "string" && checkpoint.idempotencyKey.trim()
            ? checkpoint.idempotencyKey
            : undefined,
        payloadSignature: typeof checkpoint.payloadSignature === "string" && checkpoint.payloadSignature.trim()
            ? checkpoint.payloadSignature
            : undefined,
        variables: checkpoint.variables.filter(variable => typeof variable === "string"),
        originalSeedValues: Object.keys(originalSeedValues).reduce((values, variable) => {
            const value = originalSeedValues[variable];
            if (typeof value === "string") {
                values[variable] = value;
            }
            return values;
        }, {} as Record<string, string>),
        preSentinelValues: Object.keys(preSentinelValues).reduce((values, variable) => {
            const value = preSentinelValues[variable];
            if (typeof value === "string") {
                values[variable] = value;
            }
            return values;
        }, {} as Record<string, string>),
        targetLocationApplicationIds: targetLocationApplicationIds.filter(applicationId => typeof applicationId === "string"),
        templateConfigurationId: checkpoint.templateConfigurationId,
        coreTemplateConfigurationId: typeof checkpoint.coreTemplateConfigurationId === "string" && checkpoint.coreTemplateConfigurationId
            ? checkpoint.coreTemplateConfigurationId
            : undefined
    };
};

export const setOnboardingSubmissionCheckpoint = (
    detailsJson: string | undefined,
    checkpoint?: OnboardingSubmissionCheckpoint
): string => {
    const details = parseOnboardingProgressDetails(detailsJson);

    if (checkpoint) {
        return serializeOnboardingProgressDetails({
            ...details,
            submissionCheckpoint: checkpoint
        });
    }

    const { submissionCheckpoint, ...detailsWithoutCheckpoint } = details;
    return serializeOnboardingProgressDetails(detailsWithoutCheckpoint);
};

export const getOnboardingProgressPhase = (progress?: OnboardingProgressModel | null): OnboardingPhase => {
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    const emailTriggers = details.emailTriggers || {};

    if (
        emailTriggers['testing-ready']?.status === 'Sent' ||
        emailTriggers['phase2-testing-ready']?.status === 'Sent'
    ) {
        return "testing";
    }

    if (
        emailTriggers['testing-ready']?.status === 'Pending' ||
        emailTriggers['testing-ready']?.status === 'Skipped'
    ) {
        return "onboarding";
    }

    if (details.phase === "testing") {
        return "testing";
    }

    return "onboarding";
};

export const getTestingProgressState = (progress?: OnboardingProgressModel | null): TestingProgressState => {
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    const testing = details.testing && typeof details.testing === "object" ? details.testing : {};
    const rounds = Array.isArray((testing as any).rounds) ? (testing as any).rounds : [];
    const completedStepIds = Array.isArray(testing.completedStepIds) ? testing.completedStepIds : [];
    const phase2InviteWasSent = details.emailTriggers?.['phase2-testing-ready']?.status === 'Sent';

    // Older Round 1 resolutions cleared the completed steps but left the cursor and
    // completion timestamp on the final page. Recover those records when the Round 2
    // invite is opened so the client starts the new pass at its first testing step.
    if (
        phase2InviteWasSent
        && rounds.length === 1
        && completedStepIds.length === 0
        && testing.currentStepId === 'ready-to-go'
    ) {
        return {
            ...testing,
            currentStepId: undefined,
            completedAt: undefined
        };
    }

    return testing;
};

export const getTestingFeedbackResolutionState = (
    progress?: OnboardingProgressModel | null
): TestingFeedbackResolutionState => {
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    const testing = details.testing && typeof details.testing === "object" && !Array.isArray(details.testing)
        ? details.testing
        : {};
    const fixedStatus = testing.feedbackFixedStatus || details.feedbackFixedStatus;
    const internalFixNotes = testing.feedbackInternalFixNotes || details.feedbackInternalFixNotes;

    return {
        fixedStatus: fixedStatus && typeof fixedStatus === "object" && !Array.isArray(fixedStatus)
            ? fixedStatus
            : {},
        internalFixNotes: internalFixNotes && typeof internalFixNotes === "object" && !Array.isArray(internalFixNotes)
            ? internalFixNotes
            : {}
    };
};

export const setTestingFeedbackResolutionState = (
    details: OnboardingProgressDetails,
    resolution: TestingFeedbackResolutionState
): OnboardingProgressDetails => {
    const {
        feedbackFixedStatus: legacyFixedStatus,
        feedbackInternalFixNotes: legacyInternalFixNotes,
        ...detailsWithoutLegacyResolution
    } = details;

    return {
        ...detailsWithoutLegacyResolution,
        testing: {
            ...(details.testing || {}),
            feedbackFixedStatus: resolution.fixedStatus,
            feedbackInternalFixNotes: resolution.internalFixNotes
        }
    };
};

export const getTestingStepFeedback = (progress?: OnboardingProgressModel | null): Record<string, string> => {
    const testing = getTestingProgressState(progress);
    const feedback = testing.stepFeedbackByStepId;

    return feedback && typeof feedback === "object" && !Array.isArray(feedback) ? feedback : {};
};

export const getTestingStepFeedbackHistory = (progress?: OnboardingProgressModel | null): Record<string, TestingStepFeedbackHistoryEntry[]> => {
    const testing = getTestingProgressState(progress);
    const historyByStepId = testing.stepFeedbackHistoryByStepId;

    if (!historyByStepId || typeof historyByStepId !== "object" || Array.isArray(historyByStepId)) {
        return {};
    }

    return Object.keys(historyByStepId).reduce((accumulator, stepId) => {
        const entries = historyByStepId[stepId];
        if (!Array.isArray(entries)) {
            return accumulator;
        }

        const validEntries = entries.filter(entry => {
            return !!entry
                && typeof entry === "object"
                && typeof entry.value === "string"
                && typeof entry.savedAt === "string"
                && !!entry.savedAt.trim();
        });

        if (validEntries.length > 0) {
            accumulator[stepId] = validEntries;
        }

        return accumulator;
    }, {} as Record<string, TestingStepFeedbackHistoryEntry[]>);
};

export const getOnboardingProgressAnswers = (progress?: OnboardingProgressModel | null): Record<string, unknown> => {
    const parsedDetails = parseOnboardingProgressDetails(progress?.detailsJson);
    const nestedAnswers = parsedDetails.answers;

    if (nestedAnswers && typeof nestedAnswers === "object" && !Array.isArray(nestedAnswers)) {
        return nestedAnswers as Record<string, unknown>;
    }

    return Object.keys(parsedDetails).reduce((accumulator, key) => {
        if (["answers", "flowType", "lastSavedAt", "phase", "testing", "onboardingSnapshot", "testingEnabledAt", "submissionCheckpoint", "feedbackFixedStatus", "feedbackInternalFixNotes"].includes(key)) {
            return accumulator;
        }

        accumulator[key] = parsedDetails[key];
        return accumulator;
    }, {} as Record<string, unknown>);
};
